import {
  MUTATE_OBJECT_KEY,
  MUTATE_REMOVE_OBJECT_KEY,
  APPEND,
} from '@/store/modules/mutation_types';
import { displaceDateRange } from '@/utils/url';
import BlipService from '@/services/BlipService';
import WarRoomService from '@/services/WarRoomService';
import IbovespaService from '@/services/IbovespaService';
import MediaService from '@/services/MediaService';
import OperatorsLogsService from '@/services/OperatorsLogsService';
import PerformanceService from '@/services/PerformanceService';
import ProprietaryMetricsService from '@/services/ProprietaryMetricsService';
import * as ChartsService from '@/services/charts-service';
import merge from 'lodash/merge';
import moment from 'moment';
import { NORMALIZED_PATTERN, NORMALIZED_MIN_PATTERN_DASHED } from '@/utils/date';
import { DATE_TIME } from '@/utils/constants';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import SeStudioEndpointService from '@/services/SeStudioEndpointService';
import ImporterService from '@/services/ImporterService';
import CallExportsService from '@/services/CallExportsService';
import TotalCallsService from '@/services/TotalCallsService';
import SheetImporterService from '@/services/SheetImporterService';
import SacReportsService from '@/services/SacReportsService';
import SheetImporterTagsService from '@/services/SheetImporterTagsService';
import firebase from '@/plugins/firebase';

const db = firebase.firestore();

function getDefaultParams({ rootGetters, rootState }) {
  return {
    date_range: rootGetters['filter/formattedDateRange'],
    account_id: rootState.filter.accountId,
    universe_id: rootState.filter.universeId,
    is_staging: rootState.dashboard.isStaging,
  };
}

function isCategoryEmpty(filter) {
  return !Boolean(filter?.category?.value);
}

function isBotEmpty(filter) {
  return !Boolean(filter?.bots?.value) && !Boolean(filter?.bots?.value?.length);
}

function isContractEmpty(filter) {
  return !Boolean(filter?.contracts?.value);
}

function isContractAndBotEmpty(filter) {
  return isBotEmpty(filter) || isContractEmpty(filter);
}

function getBlipParams({ rootGetters, rootState }, options) {
  const {
    interval,
    useDisplaceDate,
    itemId,
    take,
    scheduled,
  } = options;

  let startDate = rootGetters['filter/getStarDashedDate'];
  let endDate = rootGetters['filter/getEndDashedDate'];

  if (useDisplaceDate) {
    const params = {
      date_range: `${moment(startDate).format(NORMALIZED_PATTERN)}:${moment(endDate).format(NORMALIZED_PATTERN)}`,
    };

    const { displacedParams } = displaceDateRange(params);
    const [previousDate, currentDate] = displacedParams.date_range.split(':');

    startDate = moment(previousDate, NORMALIZED_PATTERN).format(NORMALIZED_MIN_PATTERN_DASHED);
    endDate = moment(currentDate, NORMALIZED_PATTERN).format(NORMALIZED_MIN_PATTERN_DASHED);
  }

  let { filter } = getCardProps(rootGetters, itemId);

  if (isContractAndBotEmpty(filter) || isCategoryEmpty(filter)) filter = { ...rootState.filter.globalFilter, ...filter };

  if (isContractAndBotEmpty(filter)) throw new Error('emptyBot');

  const filterCopy = { ...filter };

  delete filterCopy.bots;
  delete filterCopy.contracts;

  const filterNames = Object.keys(filterCopy);

  const optionalParams = filterNames.reduce((filterObject, filterName) => ({ ...filterObject, [filterName]: filterCopy[filterName].value }), {});

  return filter.bots?.value?.map(bot => {
    return {
      ...optionalParams,
      startDate,
      endDate,
      interval,
      take,
      scheduled,
      commandUrl: `https://${filter?.contracts?.value}.http.msging.net/commands`,
      botKey: bot,
    };
  });
}

function getBlipParamsWithOptionalParams({ rootGetters, rootState }, options) {
  const {
    itemId,
    take,
    categoryFilter,
    botFilteredKey,
    contractFiltered,
  } = options;

  let { filter } = getCardProps(rootGetters, itemId);

  if (isContractAndBotParamsEmpty(contractFiltered, botFilteredKey)) {
    if (isContractAndBotEmpty(filter) || isCategoryEmpty(filter)) filter = { ...rootState.filter.globalFilter, ...filter };
    if (isContractAndBotEmpty(filter)) throw new Error('emptyBot');
  }

  const { botKey, contract } = validateBotKeyAndContract(botFilteredKey, filter, contractFiltered);

  return [{
    take,
    categoryFilter,
    category: filter?.category?.value,
    commandUrl: `https://${contract}.http.msging.net/commands`,
    botKey,
  }];
}

function getDeskParams(options) {
  const {
    bots,
    contracts,
  } = options;

  return bots?.map(bot => ({
    commandUrl: `https://${contracts}.http.msging.net/commands`,
    botKey: bot,
  }));
}

function isContractAndBotParamsEmpty(contract, bot) {
  return !Boolean(contract) || !Boolean(bot);
}

function validateContractParameter(contractFiltered, filter) {
  const contract = Boolean(contractFiltered) ? contractFiltered : filter?.contracts?.value;
  return contract;
}

function validateBotKeyAndContract(botFilteredKey, filter, contractFiltered) {
  const botKey = Boolean(botFilteredKey) ? botFilteredKey[0] : filter?.bots?.value[0];
  const contract = validateContractParameter(contractFiltered, filter);

  return { botKey, contract };
}

function successHandler({ keyToRequest, commit }) {
  return data => {
    commit(MUTATE_OBJECT_KEY, {
      property: 'dataFetched',
      key: keyToRequest,
      with: data,
    });
    return Promise.resolve(data);
  };
}

function errorHandler({ keyToRequest, commit }) {
  return (e) => {
    commit(MUTATE_REMOVE_OBJECT_KEY, {
      property: 'dataFetched',
      key: keyToRequest,
    });
    return Promise.reject(e);
  };
}

async function loadCustomComponentsFromDatabase() {
  try {
    const cardsMenuCollection = db.collection('cards');
    const customCards = await cardsMenuCollection.doc('custom').get();

    return customCards.data().components || [];
  } catch (err) {
    console.error('Loading custom components from DB failed: ', err);
  }
  return [];
}

export async function updateAllowCardsFromDatabase({ commit }) {
  const customCards = await loadCustomComponentsFromDatabase();

  commit(APPEND, {
    property: 'allowCards',
    with: customCards,
  });
}

function getCard(rootGetters, itemId) {
  return rootGetters['dashboard/getCardByItemId'](itemId);
}

function getCardProps(rootGetters, itemId) {
  const card = rootGetters['dashboard/getCardByItemId'](itemId);

  return card?.props || {};
}

export function hasNoBlipBotAndContractFiltered({ rootGetters, rootState }, itemId) {
  const card = getCardProps(rootGetters, itemId);

  const hasBots = Boolean(!card.filter?.bots?.value) && Boolean(!rootState.filter.globalFilter.bots?.value);
  const hasContracts = Boolean(!card.filter?.contracts?.value) && Boolean(!rootState.filter.globalFilter.contracts?.value);

  return Boolean(hasBots || hasContracts);
}

export function getAppliedCardFilter({ rootGetters, rootState }, itemId) {
  const cardFilter = getCardProps(rootGetters, itemId)?.filter;
  return { ...rootState.filter.globalFilter, ...cardFilter };
}

async function getParams({ rootState, rootGetters, dispatch }, options = {}) {
  const { itemId } = options;
  const defaultParams = getDefaultParams({ rootGetters, rootState });

  const serviceParams = { ...options.serviceParams };
  const card = getCard(rootGetters, itemId);

  const cardFilters = card.props?.filter;
  serviceParams.filter = JSON.stringify(merge({}, serviceParams.filter, cardFilters));

  const newCardFilter = await dispatch('filter/applyGlobalFilter', { card }, { root: true });
  if (options.ignoreThemeFilter) {
    delete newCardFilter.themes;
  }

  const filterId = !options.ignoreThemeFilter && card.props.filterId
    ? card.props.filterId
    : await WarRoomService.encodeFilter({
      ...defaultParams,
      filter: JSON.stringify(newCardFilter),
    });

  delete serviceParams.filter;
  serviceParams.filter_id = filterId;
  return {
    ...defaultParams,
    ...serviceParams,
  };
}

async function getSacListeningParams({ rootState, rootGetters, dispatch }, options = {}) {
  const { itemId, filterType } = options;
  const defaultParams = getDefaultParams({ rootGetters, rootState });
  let serviceParams = { ...options.serviceParams };
  const card = getCard(rootGetters, itemId);
  let newCardFilter = await dispatch('filter/applyGlobalFilter', { card, filterType }, { root: true });
  ({ newCardFilter, serviceParams } = deleteUnusedFilter(newCardFilter, serviceParams));
  if (filterType === DATA_TYPE.LISTENING) {
    const cardFilters = card.props.filter?.listeningFilter;
    serviceParams.filter = JSON.stringify(merge({}, serviceParams.filter, cardFilters));

    const listeningFilterId = card.props.filter?.listeningFilterId || await WarRoomService.encodeFilter({
      ...defaultParams,
      filter: JSON.stringify(newCardFilter),
    });

    delete serviceParams.filter;
    serviceParams.filter_id = listeningFilterId;
  } else if (filterType === DATA_TYPE.SAC) {
    const cardFilters = card.props.filter?.sacFilter;
    serviceParams.filter = JSON.stringify(merge({}, serviceParams.filter, cardFilters));

    const sacFilterId = card.props.filter?.sacFilterId || await WarRoomService.encodeFilter({
      ...defaultParams,
      filter: JSON.stringify(newCardFilter),
    });

    delete serviceParams.filter;
    serviceParams.filter_id = sacFilterId;
  }
  return {
    ...defaultParams,
    ...serviceParams,
  };
}

function deleteUnusedFilter(newCardFilter, serviceParams) {
  if (serviceParams.favorite !== undefined || serviceParams.totalFavorite) {
    newCardFilter.favorite = { value: true };
  }
  if (serviceParams.noFilter || serviceParams.totalFavorite) {
    delete newCardFilter.themes;
    delete serviceParams.noFilter;
    delete serviceParams.totalFavorite;
  }
  return {
    newCardFilter,
    serviceParams,
  };
}

async function serviceBlipRequest(service, { rootState, rootGetters, commit, dispatch }, options = {}) {
  const { servicePath, optionalParams } = options;

  const paramsStack = Boolean(optionalParams) ? await getBlipParamsWithOptionalParams({ rootState, rootGetters, dispatch }, options)
    : await getBlipParams({ rootState, rootGetters, dispatch }, options);

  const requestsStack = paramsStack.map(params => service[servicePath]({ params }));

  const keyToRequest = JSON.stringify({
    ...requestsStack,
    request: servicePath,
  });

  return Promise.all(requestsStack)
    .then(successHandler({ keyToRequest, commit }))
    .catch(errorHandler({ keyToRequest, commit }));
}

async function serviceDeskRequest(service, { commit }, options = {}) {
  const { servicePath } = options;

  const paramsStack = getDeskParams(options);

  const requestsStack = paramsStack.map(params => service[servicePath]({ params }));

  const keyToRequest = JSON.stringify({
    ...requestsStack,
    request: servicePath,
  });

  return Promise.all(requestsStack)
    .then(successHandler({ keyToRequest, commit }))
    .catch(errorHandler({ keyToRequest, commit }));
}

async function serviceRequest(service, { rootState, rootGetters, commit, dispatch }, options = {}) {
  const { servicePath } = options;

  let params = await getDefaultParams({ rootState, rootGetters, dispatch }, options);
  if (options.stockParams) {
    params.stockParams = options.stockParams;
  }
  if (options.itemId) {
    params = await getParams({ rootState, rootGetters, dispatch }, options);
  } else {
    params.q = options.serviceParams?.q;
  }

  const keyToRequest = JSON.stringify({
    ...params,
    request: servicePath,
  });

  return service[servicePath]({ params })
    .then(successHandler({ keyToRequest, commit }))
    .catch(errorHandler({ keyToRequest, commit }));
}

async function getPageMetricsParams({ rootState, rootGetters }, options = {}) {
  const { itemId } = options;
  const defaultParams = getDefaultParams({ rootGetters, rootState });
  const serviceParams = { ...options.serviceParams };

  const pageFilter = merge({}, serviceParams.filter, getCardProps(rootGetters, itemId).filter);

  return {
    ...defaultParams,
    ...serviceParams,
    pageFilter,
  };
}

export async function serviceRequestPageMetrics(service, { rootState, rootGetters, commit }, options = {}) {
  const { servicePath } = options;

  let params = await getPageMetricsParams({ rootState, rootGetters }, options);
  const pages = rootGetters['dashboard/pages'];

  if (options.useDisplaceDate) {
    params = displaceDateRange(params).displacedParams;
  }

  if (options.usePreviousDay) {
    const [start, end] = params.date_range.split(':');
    const newStart = moment(start, NORMALIZED_PATTERN).subtract(1, DATE_TIME.DAYS).format(NORMALIZED_PATTERN);

    params.date_range = `${newStart}:${end}`;
  }

  const globalFilter = rootGetters['filter/pageMetricsFilter'];

  let filteredPages = pages;
  let filtersReference = null;

  if (params.pageFilter.pages?.value.length > 0) {
    filtersReference = params.pageFilter.pages.value;
  } else if (globalFilter.pages?.value.length > 0) {
    filtersReference = globalFilter.pages.value;
  }

  if (filtersReference) {
    filteredPages = pages
      .filter(page => filtersReference.includes(page.page_id));
  }

  const promises = filteredPages.map(page => service[servicePath]({
    params: {
      ...params,
      channel: page.channel,
      page_id: page.page_id,
    },
  }));

  const keyToRequest = JSON.stringify({
    ...params,
    request: servicePath,
  });

  return Promise.allSettled(promises)
    .then(successHandler({ keyToRequest, commit }))
    .catch(errorHandler({ keyToRequest, commit }));
}

export function getBlipService(store, options = {}) {
  return serviceBlipRequest(BlipService, store, options);
}

export function getDeskService(store, options = {}) {
  return serviceDeskRequest(BlipService, store, options);
}

export async function getBlipServiceWithVariation(store, options = {}) {
  const [displacedData, data] = await Promise.all([
    serviceBlipRequest(BlipService, store, {
      ...options,
      useDisplaceDate: true,
    }),
    serviceBlipRequest(BlipService, store, options),
  ]);

  return merge({}, { currentData: data }, { previousData: displacedData });
}

async function serviceSacListeningRequest(service, { rootState, rootGetters, commit, dispatch }, options = {}) {
  const { servicePath } = options;

  let params = await getDefaultParams({ rootState, rootGetters, dispatch }, options);

  params = await getSacListeningParams({ rootState, rootGetters, dispatch }, options);

  const keyToRequest = JSON.stringify({
    ...params,
    request: servicePath,
  });

  return service[servicePath]({ params })
    .then(successHandler({ keyToRequest, commit }))
    .catch(errorHandler({ keyToRequest, commit }));
}

export function getWarroomService(store, options = {}) {
  return serviceRequest(WarRoomService, store, options);
}

export function getSacListeningWarroomService(store, options = {}) {
  return serviceSacListeningRequest(WarRoomService, store, options);
}

export function getIbovespaService(store, options = {}) {
  return serviceRequest(IbovespaService, store, options);
}

export function getMediaService(store, options = {}) {
  return serviceRequest(MediaService, store, options);
}

export function getOperatorsLogsService(store, options = {}) {
  return serviceRequest(OperatorsLogsService, store, options);
}

export function getCallExportsService(store, options = {}) {
  return serviceRequest(CallExportsService, store, options);
}

export function getTotalCallsService(store, options = {}) {
  return serviceRequest(TotalCallsService, store, options);
}

export function getPerformanceService(store, options = {}) {
  return serviceRequest(PerformanceService, store, options);
}

export function getProprietaryMetricsService(store, options = {}) {
  return serviceRequest(ProprietaryMetricsService, store, options);
}

export function getPerformancePageMetricsService(store, options = {}) {
  return serviceRequestPageMetrics(PerformanceService, store, options);
}

export function getSacReportsService(store, options = {}) {
  return serviceRequest(SacReportsService, store, options);
}

export function getSeStudioEndpointService(store, options = {}) {
  return serviceRequest(SeStudioEndpointService, store, options);
}

export function getImporterService(store, options = {}) {
  return serviceRequest(ImporterService, store, options);
}

export function getSheetImporterService(store, options = {}) {
  return serviceRequest(SheetImporterService, store, options);
}

export function getSheetImporterTagsService(store, options = {}) {
  return serviceRequest(SheetImporterTagsService, store, options);
}

export async function getPerformancePageMetricsServiceWithVariation(store, options = {}) {
  const series = [
    serviceRequestPageMetrics(PerformanceService, store, {
      ...options,
      useDisplaceDate: true,
    }),
    serviceRequestPageMetrics(PerformanceService, store, options),
  ];
  const resolveSeries = await Promise.all(series);
  return resolveSeries;
}

export async function getChartsService(store, options = {}) {
  const {
    method,
    entity = ChartsService.ENTITIES.POSTS,
    points_parser = 'series',
    itemId,
  } = options;

  const card = getCard(store.rootGetters, itemId);

  let params;
  if (card.dataType !== DATA_TYPE.SAC_LISTENING) {
    params = await getParams(store, {
      ...options,
      method,
    });
  } else {
    params = await getSacListeningParams(store, {
      ...options,
      method,
    });
  }

  if (options.increaseEndDay) {
    const [start, end] = params.date_range.split(':');
    const newEnd = moment(end, NORMALIZED_PATTERN).add(1, DATE_TIME.DAYS).format(NORMALIZED_PATTERN);

    params.date_range = `${start}:${newEnd}`;
  }

  const keyToRequest = JSON.stringify({
    ...params,
    request: method,
  });
  const handlerParams = {
    ...store,
    keyToRequest,
  };

  return ChartsService[entity][method]({
    ...options.params,
    params: {
      ...params,
      points_parser,
    },
  })
    .then(successHandler(handlerParams))
    .catch(errorHandler(handlerParams));
}
